import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Sparkles, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";

const blank = {
  lead_id: "", customer_name: "", company: "", email: "", phone: "", address: "", gstin: "",
  product_id: "", equipment: "", model_no: "", serial_no: "", accessories: "", under_warranty: false,
  complaint: "", inspection: "", diagnosis: "", parts: [], labour: [], other_costs: [],
  discount: 0, advance_amount: 0, warranty_months: 3, expected_delivery: "", valid_until: "",
  terms: "", notes: "", engineer_id: "", revision_reason: "",
};

const lineTotal = (form) => {
  const p = (form.parts || []).reduce((a, x) => a + Number(x.qty || 0) * Number(x.price || 0), 0);
  const l = (form.labour || []).reduce((a, x) => a + Number(x.hours || 0) * Number(x.rate || 0), 0);
  const o = (form.other_costs || []).reduce((a, x) => a + Number(x.amount || 0), 0);
  const sub = p + l + o;
  const tax = (form.parts || []).reduce((a, x) => a + Number(x.qty || 0) * Number(x.price || 0) * Number(x.tax_rate || 0) / 100, 0)
    + (form.labour || []).reduce((a, x) => a + Number(x.hours || 0) * Number(x.rate || 0) * Number(x.tax_rate || 0) / 100, 0)
    + (form.other_costs || []).reduce((a, x) => a + Number(x.amount || 0) * Number(x.tax_rate || 0) / 100, 0);
  return { sub, tax, total: Math.max(0, sub + tax - Number(form.discount || 0)) };
};

export const RepairForm = ({ open, job, meta, onOpenChange, onSaved }) => {
  const editing = !!job;
  const [form, setForm] = useState(blank);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    if (!open) return;
    const d = meta?.defaults || {};
    setForm(editing ? { ...blank, ...job, revision_reason: "" }
      : { ...blank, warranty_months: d.warranty_months ?? 3, terms: d.terms || "" });
    setSuggestion(null);
    api.get("/demos/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, [open, job, editing, meta]);

  const rate = meta?.defaults?.labour_rate || 750;
  const sums = useMemo(() => lineTotal(form), [form]);

  const pickContact = (id) => {
    const c = contacts.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({
      ...f, lead_id: c.id, customer_name: c.customer_name, company: c.company || "",
      email: c.email || f.email, phone: c.phone || f.phone,
      address: f.address || [c.city, c.state].filter(Boolean).join(", "),
    }));
  };

  const pickProduct = (id) => {
    const p = products.find((x) => x.id === id);
    if (!p) return;
    setForm((f) => ({ ...f, product_id: p.id, equipment: p.name, model_no: f.model_no || p.model_no || p.sku }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addRow = (k, row) => setForm((f) => ({ ...f, [k]: [...(f[k] || []), row] }));
  const editRow = (k, i, patch) => setForm((f) => ({
    ...f, [k]: f[k].map((x, j) => (j === i ? { ...x, ...patch } : x)),
  }));
  const dropRow = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, j) => j !== i) }));

  const suggest = async () => {
    setThinking(true);
    try {
      const { data } = await api.post("/repairs/ai-suggest", {
        equipment: form.equipment, model_no: form.model_no,
        complaint: form.complaint, inspection: form.inspection,
      });
      setSuggestion(data);
      toast.success(data.history_used
        ? `Suggested from ${data.history_used} similar past repair(s)`
        : "Suggested - no similar past repair on record yet");
    } catch (e) { toast.error(errMsg(e)); } finally { setThinking(false); }
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((f) => ({
      ...f,
      diagnosis: f.diagnosis || suggestion.diagnosis || "",
      parts: [...(f.parts || []), ...(suggestion.parts || []).map((p) => {
        const known = products.find((x) => x.name.toLowerCase() === String(p.name || "").toLowerCase());
        return {
          product_id: known?.id || "", name: known?.name || p.name,
          qty: Number(p.qty || 1), price: Number(known?.selling_price ?? p.price ?? 0),
          tax_rate: Number(known?.tax_rate ?? 18), remarks: p.reason || "",
        };
      })],
      labour: [...(f.labour || []), ...(suggestion.labour || []).map((l) => ({
        description: l.description, hours: Number(l.hours || 1),
        rate: Number(l.rate || rate), tax_rate: 18,
      }))],
      notes: f.notes || suggestion.notes || "",
    }));
    toast.success("Added to the estimate - edit anything you disagree with");
    setSuggestion(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Choose or type the customer.");
    if (!form.complaint.trim()) return toast.error("Record what the customer reported.");
    if (editing && job.status !== "Draft" && !String(form.revision_reason || "").trim()) {
      return toast.error("Please say why this estimate is changing.");
    }
    setBusy(true);
    const payload = {
      ...form, discount: Number(form.discount || 0), advance_amount: Number(form.advance_amount || 0),
      warranty_months: Number(form.warranty_months || 0),
      parts: (form.parts || []).map((p) => ({ ...p, qty: Number(p.qty || 0), price: Number(p.price || 0), tax_rate: Number(p.tax_rate || 18) })),
      labour: (form.labour || []).map((l) => ({ ...l, hours: Number(l.hours || 0), rate: Number(l.rate || 0), tax_rate: Number(l.tax_rate || 18) })),
      other_costs: (form.other_costs || []).map((o) => ({ ...o, amount: Number(o.amount || 0), tax_rate: Number(o.tax_rate || 18) })),
    };
    try {
      if (editing) {
        await api.patch(`/repairs/${job.id}`, payload);
        toast.success(job.status === "Draft" ? "Estimate updated" : "Saved as a new revision");
        onSaved?.(job.id);
      } else {
        const { data } = await api.post("/repairs", payload);
        toast.success(`Estimate ${data.number} created`);
        onSaved?.(data.id);
      }
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? `Edit ${job.number}` : "New repair estimate"}
          </DialogTitle>
          <DialogDescription>
            Record the equipment and the fault, then build the estimate. The same record becomes the
            service job and later the invoice.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Customer or CRM lead" required hint="Or just type a name below for a walk-in.">
              <Select value={form.lead_id} onValueChange={pickContact}>
                <SelectTrigger data-testid="rf-contact">
                  <SelectValue placeholder={form.customer_name || "Select a customer or lead"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Customers</SelectLabel>
                    {contacts.filter((c) => c.kind === "customer").map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.customer_name}{c.company ? ` \u00b7 ${c.company}` : ""}</SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>CRM leads</SelectLabel>
                    {contacts.filter((c) => c.kind === "lead").map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.customer_name}{c.company ? ` \u00b7 ${c.company}` : ""}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Customer name" required>
              <Input value={form.customer_name} data-testid="rf-customer"
                onChange={(e) => set("customer_name", e.target.value)} />
            </Field>
            <Field label="Company">
              <Input value={form.company} data-testid="rf-company" onChange={(e) => set("company", e.target.value)} />
            </Field>
            <Field label="Email" hint="The estimate and approval link go here.">
              <Input type="email" value={form.email} data-testid="rf-email" onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} data-testid="rf-phone" onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field label="Engineer">
              <Select value={form.engineer_id} onValueChange={(v) => set("engineer_id", v)}>
                <SelectTrigger data-testid="rf-engineer"><SelectValue placeholder="Assign later" /></SelectTrigger>
                <SelectContent>
                  {(meta?.engineers || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid gap-4 rounded-xl border border-border bg-muted/30 p-3 sm:grid-cols-2">
            <Field label="Equipment from the catalogue">
              <Select value={form.product_id} onValueChange={pickProduct}>
                <SelectTrigger data-testid="rf-product"><SelectValue placeholder="Pick a product" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Equipment" required>
              <Input value={form.equipment} data-testid="rf-equipment" onChange={(e) => set("equipment", e.target.value)}
                placeholder="Falcon X7 Survey Drone" />
            </Field>
            <Field label="Model number">
              <Input value={form.model_no} data-testid="rf-model" onChange={(e) => set("model_no", e.target.value)} />
            </Field>
            <Field label="Serial number">
              <Input value={form.serial_no} data-testid="rf-serial" onChange={(e) => set("serial_no", e.target.value)} />
            </Field>
            <Field label="Accessories received">
              <Input value={form.accessories} data-testid="rf-accessories" onChange={(e) => set("accessories", e.target.value)}
                placeholder="2 batteries, charger, propellers" />
            </Field>
            <label className="flex cursor-pointer items-end gap-2 pb-2 text-sm font-medium">
              <Checkbox checked={form.under_warranty} data-testid="rf-warranty-in"
                onCheckedChange={(v) => set("under_warranty", !!v)} />
              Still under warranty
            </label>
          </div>

          <Field label="Customer complaint" required>
            <Textarea rows={2} value={form.complaint} data-testid="rf-complaint"
              onChange={(e) => set("complaint", e.target.value)}
              placeholder="Drone drifts to the left and the gimbal shakes in flight." />
          </Field>
          <Field label="Inspection findings">
            <Textarea rows={2} value={form.inspection} data-testid="rf-inspection"
              onChange={(e) => set("inspection", e.target.value)} />
          </Field>

          <div className="rounded-xl border border-primary/25 bg-primary-soft/40 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="soft" disabled={thinking} data-testid="rf-ai-suggest" onClick={suggest}>
                {thinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Suggest the repair
              </Button>
              <span className="text-xs text-muted-foreground">
                Reads your past repairs of this model and the product catalogue. You stay in control.
              </span>
            </div>
            {suggestion && (
              <div className="mt-3 space-y-2 rounded-lg border border-border bg-card p-3" data-testid="rf-ai-result">
                {!!(suggestion.likely_faults || []).length && (
                  <p className="text-sm"><b>Likely faults:</b> {suggestion.likely_faults.join("; ")}</p>
                )}
                {suggestion.diagnosis && <p className="text-sm text-muted-foreground">{suggestion.diagnosis}</p>}
                {!!(suggestion.parts || []).length && (
                  <p className="text-xs text-muted-foreground">
                    <b>Parts:</b> {suggestion.parts.map((p) => `${p.name} x${p.qty}${p.in_catalogue ? "" : " (new)"}`).join(", ")}
                  </p>
                )}
                {!!(suggestion.labour || []).length && (
                  <p className="text-xs text-muted-foreground">
                    <b>Service:</b> {suggestion.labour.map((l) => `${l.description} (${l.hours} hr)`).join(", ")}
                  </p>
                )}
                {suggestion.notes && <p className="text-xs italic text-muted-foreground">{suggestion.notes}</p>}
                <div className="flex gap-2">
                  <Button type="button" size="sm" data-testid="rf-ai-apply" onClick={applySuggestion}>
                    <Plus className="h-3.5 w-3.5" /> Add to the estimate
                  </Button>
                  <Button type="button" size="sm" variant="ghost" data-testid="rf-ai-dismiss" onClick={() => setSuggestion(null)}>Dismiss</Button>
                </div>
              </div>
            )}
          </div>

          <Field label="Diagnosis for the estimate">
            <Textarea rows={2} value={form.diagnosis} data-testid="rf-diagnosis"
              onChange={(e) => set("diagnosis", e.target.value)} />
          </Field>

          {/* parts */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Parts required</p>
            {(form.parts || []).map((p, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-12">
                <Input className="sm:col-span-5" placeholder="Part name" value={p.name} data-testid={`rf-part-name-${i}`}
                  onChange={(e) => editRow("parts", i, { name: e.target.value })} />
                <Input className="sm:col-span-2" type="number" min="0" step="0.5" placeholder="Qty" value={p.qty}
                  data-testid={`rf-part-qty-${i}`} onChange={(e) => editRow("parts", i, { qty: e.target.value })} />
                <Input className="sm:col-span-2" type="number" min="0" placeholder="Rate" value={p.price}
                  data-testid={`rf-part-price-${i}`} onChange={(e) => editRow("parts", i, { price: e.target.value })} />
                <Input className="sm:col-span-2" type="number" min="0" placeholder="GST %" value={p.tax_rate}
                  data-testid={`rf-part-tax-${i}`} onChange={(e) => editRow("parts", i, { tax_rate: e.target.value })} />
                <Button type="button" size="iconSm" variant="ghost" className="sm:col-span-1"
                  data-testid={`rf-part-remove-${i}`} onClick={() => dropRow("parts", i)}>
                  <X className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Select value="" onValueChange={(id) => {
                const p = products.find((x) => x.id === id);
                if (p) addRow("parts", { product_id: p.id, name: p.name, qty: 1, price: p.selling_price, tax_rate: p.tax_rate ?? 18, remarks: "" });
              }}>
                <SelectTrigger className="w-[240px]" data-testid="rf-part-picker"><SelectValue placeholder="Add a part from stock" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" size="sm" variant="outline" data-testid="rf-part-add"
                onClick={() => addRow("parts", { product_id: "", name: "", qty: 1, price: 0, tax_rate: 18, remarks: "" })}>
                <Plus className="h-3.5 w-3.5" /> Other part
              </Button>
            </div>
          </div>

          {/* labour */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Service and labour</p>
            {(form.labour || []).map((l, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-12">
                <Input className="sm:col-span-6" placeholder="Work description" value={l.description}
                  data-testid={`rf-labour-desc-${i}`} onChange={(e) => editRow("labour", i, { description: e.target.value })} />
                <Input className="sm:col-span-2" type="number" min="0" step="0.5" placeholder="Hours" value={l.hours}
                  data-testid={`rf-labour-hours-${i}`} onChange={(e) => editRow("labour", i, { hours: e.target.value })} />
                <Input className="sm:col-span-2" type="number" min="0" placeholder="Rate/hr" value={l.rate}
                  data-testid={`rf-labour-rate-${i}`} onChange={(e) => editRow("labour", i, { rate: e.target.value })} />
                <Input className="sm:col-span-1" type="number" min="0" placeholder="GST" value={l.tax_rate}
                  data-testid={`rf-labour-tax-${i}`} onChange={(e) => editRow("labour", i, { tax_rate: e.target.value })} />
                <Button type="button" size="iconSm" variant="ghost" className="sm:col-span-1"
                  data-testid={`rf-labour-remove-${i}`} onClick={() => dropRow("labour", i)}>
                  <X className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" data-testid="rf-labour-add"
              onClick={() => addRow("labour", { description: "", hours: 1, rate, tax_rate: 18 })}>
              <Plus className="h-3.5 w-3.5" /> Add service line
            </Button>
          </div>

          {/* other */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Other charges</p>
            {(form.other_costs || []).map((o, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-12">
                <Input className="sm:col-span-7" placeholder="Courier, calibration, testing..." value={o.description}
                  data-testid={`rf-cost-desc-${i}`} onChange={(e) => editRow("other_costs", i, { description: e.target.value })} />
                <Input className="sm:col-span-2" type="number" min="0" placeholder="Amount" value={o.amount}
                  data-testid={`rf-cost-amount-${i}`} onChange={(e) => editRow("other_costs", i, { amount: e.target.value })} />
                <Input className="sm:col-span-2" type="number" min="0" placeholder="GST" value={o.tax_rate}
                  data-testid={`rf-cost-tax-${i}`} onChange={(e) => editRow("other_costs", i, { tax_rate: e.target.value })} />
                <Button type="button" size="iconSm" variant="ghost" className="sm:col-span-1"
                  data-testid={`rf-cost-remove-${i}`} onClick={() => dropRow("other_costs", i)}>
                  <X className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" data-testid="rf-cost-add"
              onClick={() => addRow("other_costs", { description: "", amount: 0, tax_rate: 18 })}>
              <Plus className="h-3.5 w-3.5" /> Add charge
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <Field label="Discount">
              <Input type="number" min="0" value={form.discount} data-testid="rf-discount"
                onChange={(e) => set("discount", e.target.value)} />
            </Field>
            <Field label="Advance payable">
              <Input type="number" min="0" value={form.advance_amount} data-testid="rf-advance"
                onChange={(e) => set("advance_amount", e.target.value)} />
            </Field>
            <Field label="Repair warranty (months)">
              <Input type="number" min="0" value={form.warranty_months} data-testid="rf-warranty-months"
                onChange={(e) => set("warranty_months", e.target.value)} />
            </Field>
            <Field label="Expected delivery">
              <Input type="date" value={form.expected_delivery} data-testid="rf-delivery"
                onChange={(e) => set("expected_delivery", e.target.value)} />
            </Field>
            <Field label="Estimate valid until">
              <Input type="date" value={form.valid_until} data-testid="rf-valid"
                onChange={(e) => set("valid_until", e.target.value)} />
            </Field>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm">
            <span className="text-muted-foreground">Subtotal <b className="num text-foreground">{money(sums.sub)}</b></span>
            <span className="text-muted-foreground">GST <b className="num text-foreground">{money(sums.tax)}</b></span>
            <span className="font-display text-lg font-bold text-primary" data-testid="rf-total">{money(sums.total)}</span>
          </div>

          <Field label="Notes for the customer">
            <Textarea rows={2} value={form.notes} data-testid="rf-notes" onChange={(e) => set("notes", e.target.value)} />
          </Field>
          <Field label="Terms and conditions">
            <Textarea rows={4} value={form.terms} data-testid="rf-terms" onChange={(e) => set("terms", e.target.value)} />
          </Field>

          {editing && job.status !== "Draft" && (
            <Field label="Why is this changing?" required hint="Saved as a new revision on the timeline.">
              <Input value={form.revision_reason} data-testid="rf-revision-reason"
                onChange={(e) => set("revision_reason", e.target.value)} />
            </Field>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" data-testid="rf-cancel" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="rf-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
              {editing ? "Save" : "Create estimate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
